/* eslint-disable no-unused-vars */
/* eslint-disable react/forbid-prop-types */
import React, { Component } from 'react';
import { select, selectAll } from 'd3-selection';
import PropTypes from 'prop-types';
import Loader from 'react-loader-spinner';
import { geoPath, geoConicConformal } from 'd3-geo';
import { scaleLinear, scaleQuantile, scaleThreshold } from 'd3-scale';
import { axisBottom } from 'd3-axis';
import { extent, max, min, range } from 'd3-array';
import { format } from 'd3-format';
import { json, mouse } from 'd3';
import { saveAs } from 'file-saver';
import { legendColor } from 'd3-svg-legend';
import 'd3-transition';
import './mapchart.scss';
import colors from '../../../../../../../../apps/baykis-pl/pattern-lab/_data/generated-colors.json';
import { setLatLonCoordinates } from '../../../../../../tokens/js/utils/functions';
import Chart from './chartitems.jsx';
import Container from './container.jsx';
import ShapeCaption from './shapecaptionitems.jsx';

// TODO: Daten für einzelne Regionen prüfen und positionieren
// TODO: Export funktioniert?

const rcpLables = {
  rcp26: 'RCP 2.6',
  rcp45: 'RCP 4.5',
  rcp85: 'RCP 8.5',
};

const d3 = {
  select,
  selectAll,
  min,
  max,
  geoPath,
  geoConicConformal,
  range,
  json,
  format,
  scaleThreshold,
  scaleQuantile,
  scaleLinear,
  legendColor,
  saveAs,
  axisBottom,
  mouse,
};

class MapChartComparison extends Component {
  svgWidth = 240;

  svgHeight = 240;

  // Extra Space um den Chart in 'pixel'
  svgPadding = 5;

  captionFirstRCP;

  captionSecondRCP;

  captionDifference;

  geopath;

  activeShape;

  constructor(props) {
    super(props);
    this.canvas = React.createRef();
    this.downloadPNG = React.createRef();
    this.placeholderMap = React.createRef();
    this.state = {
      isLoading: false,
    };
  }

  componentDidMount() {
    const { dataFirstRCP, fullShape, filteredShape } = this.props;
    if (
      dataFirstRCP?.length > 0 &&
      fullShape.length !== 0 &&
      filteredShape.length !== 0
    ) {
      // Create Path Object for active Shape
      this.extractActiveShape();
      this.buildGeoPath();

      this.buildVisualization();
    }
  }

  componentDidUpdate(prevProps) {
    const { fullShape, activeRegion } = this.props;
    if (
      prevProps.fullShape !== fullShape ||
      prevProps.activeRegion !== activeRegion
    ) {
      // Create Path Object for active Shape
      this.extractActiveShape();
      this.buildGeoPath();

      this.buildVisualization();
    }
  }

  static getUsedUnit({ colorsets }, limitToggle) {
    const [useColors] = colorsets;
    const usedUnit =
      limitToggle === 'absolute' ? useColors.absolutUnit : useColors.changeUnit;

    return usedUnit;
  }

  buildVisualization = () => {
    const {
      selectedYear,
      latLonData,
      colorsets,
      colorsetsComparison,
      firstRCP,
      secondRCP,
      dataFirstRCP,
      dataSecondRCP,
      dataComparison,
    } = this.props;

    const latLonFirstRcp = setLatLonCoordinates(dataFirstRCP, latLonData);
    const latLonSecondRcp = setLatLonCoordinates(dataSecondRCP, latLonData);
    const latLonComparison = setLatLonCoordinates(dataComparison, latLonData);

    /* TODO: refactor this.
        Bug manifests as different array lengths, which creates an exception in functions
        "calcMainData" and "calcDataPrecipitation" in functions.js.
        Reproduce like this:
        1. Open page "Klima der Vergangenheit"
        2. Select Filter "Regierungsbezirke"
        3. Choose a region on the minimap
        4. Click the button "Kartenansicht"
        5. Click on the button "Zeitansicht"
        6. Select a different Region
        7. Click the button "Kartenansicht"
        8. Repeat steps 5-7 until bug appears
        */

    if (
      dataFirstRCP === undefined ||
      dataSecondRCP === undefined ||
      dataComparison === undefined ||
      colorsets === undefined ||
      colorsetsComparison === undefined
    ) {
      return;
    }

    // Set Captions for Charts
    this.captionFirstRCP = `Änderung ${rcpLables[firstRCP]} ${selectedYear} <br />zu 1971 - 2000`;
    this.captionSecondRCP = `Änderung ${rcpLables[secondRCP]} ${selectedYear} <br />zu 1971 - 2000`;
    this.captionDifference = `Differenz<br />${rcpLables[firstRCP]} vs. ${rcpLables[secondRCP]}`;

    // Create Charts
    this.createDiagram(
      '.chart_reference',
      'legend_reference',
      latLonFirstRcp,
      'change',
      1,
      colorsets
    );
    this.createDiagram(
      '.chart_future',
      'legend_future',
      latLonSecondRcp,
      'change',
      2,
      colorsets
    );
    this.createDiagram(
      '.chart_difference',
      'legend_difference',
      latLonComparison,
      'change',
      3,
      colorsetsComparison
    );
    this.setState({ isLoading: false });
  };

  extractActiveShape = () => {
    const { fullShape, activeRegion } = this.props;
    this.activeShape = JSON.parse(
      // Index 10 for Bayern
      JSON.stringify(fullShape.features[activeRegion ? activeRegion - 1 : 10])
    );
    // TODO Marc prüfen, welches winding der Geometrie vorliegt, damit das reverse & DeepCopy erspart bleibt
    this.activeShape.geometry.coordinates[0].reverse();
  };

  buildGeoPath = () => {
    const projection = d3
      .geoConicConformal()
      .parallels([35, 65])
      .rotate([-10, 0])
      .fitSize(
        [
          this.svgWidth - this.svgPadding * 2,
          this.svgWidth - this.svgPadding * 2,
        ],
        this.activeShape
      );

    this.geopath = d3.geoPath(projection);
  };

  createLegend = (
    className,
    domainLegend,
    rangeQuantile,
    steps,
    limitToggle
  ) => {
    const svgLegendWidth = 290;
    const svgLegendHeight = 24;
    const threshold = scaleThreshold()
      .domain(domainLegend)
      .range(rangeQuantile);

    const x = d3
      .scaleThreshold()
      .domain(threshold.domain())
      .range([0, Math.round(svgLegendWidth / steps)]);
    const xAxis = d3
      .axisBottom(x)
      .tickValues(threshold.domain())
      .tickFormat((d) => d)
      .tickSize(10);

    // *** rebuild Legend SVG *** //
    const svgEl = d3.select(`.${className}`);

    const attrViewBox = `0 0 ${svgLegendWidth} ${svgLegendHeight}`;
    const legendTextSelection = svgEl.select('text');
    const legendGroupSelection = svgEl.select('g');

    if (attrViewBox !== svgEl.attr('viewBox')) {
      svgEl.attr('viewBox', attrViewBox).style('overflow', 'visible');
    }

    if (!legendTextSelection.empty()) {
      legendTextSelection.remove();
    }
    if (!legendGroupSelection.empty()) {
      legendGroupSelection.remove();
    }

    svgEl
      .append('text')
      .attr('transform', `translate(${svgLegendWidth - 25}, -17)`)
      .attr('dy', '.75em')
      .attr('fill', colors.themeBase.text)
      .attr('class', 'legend-unit')
      .style('text-anchor', 'end')
      .style('font-size', '14px')
      .text(MapChartComparison.getUsedUnit(this.props, limitToggle));

    const g = svgEl.append('g').attr('width', '20px').call(xAxis);

    g.select('.domain').remove();

    g.selectAll('rect')
      .data(
        threshold.range().map((color) => {
          const d = threshold.invertExtent(color);
          return [d, color];
        })
      )
      .enter()
      .insert('rect', '.tick')
      .attr('height', 8)
      .attr('x', (d, i) => Math.round(svgLegendWidth / steps) * i)
      .attr('width', Math.round(svgLegendWidth / steps))
      .attr('fill', (d) => d[1]);

    g.selectAll('g').each((d, i, nodes) => {
      d3.select(nodes[i])
        .attr(
          'transform',
          `translate(${Math.round(svgLegendWidth / steps) * i}, 0)`
        )
        .style('font-size', '9px')
        .attr('class', `tick tick-${i}`);
    });

    g.selectAll('g text').text((attrValue) =>
      `${attrValue}`.replace(/\./g, ',')
    );
  };

  createDiagram = (shapeSelect, className, data, limitToggle, type, colorset) => {
    this.setState({ isLoading: true });
    const { domainLegend, rangeQuantile, steps, colorScale } =
      this.generateColorScale(limitToggle, colorset);

    // *** rebuild Geoshape SVG *** //
    const svgWrapper = d3.select(`${shapeSelect} .canvas-wrapper`);

    let svg = svgWrapper.select('svg');
    if (svg.empty()) {
      svg = svgWrapper
        .append('svg')
        .attr('xmlns', 'http://www.w3.org/2000/svg')
        .attr('viewBox', `0 0 ${this.svgWidth} ${this.svgHeight}`);
    } else {
      svg.selectAll('g').remove();
    }

    // Order determines zIndex -> last drawn = on top
    const defs = svg.append('defs');
    const pixelG = svg.append('g');
    const contourG = svg.append('g');

    this.createDataLayerOnChart(data, colorScale, pixelG, defs, type);
    this.createContourLayerOnChart(defs, contourG);
    this.createLegend(
      className,
      domainLegend,
      rangeQuantile,
      steps,
      limitToggle
    );
  };

  createContourLayerOnChart(defs, contourG) {
    defs
      .append('path')
      .attr('class', 'outline')
      .attr('d', this.geopath({ type: 'Sphere' }));
    defs
      .append('clipPath')
      .attr('class', 'clip')
      .append('use')
      .attr('href', new URL('#outline', window.location));

    contourG
      .append('path')
      .attr('d', this.geopath(this.activeShape))
      .style('stroke', colors.themeBase.accentDark)
      .style('stroke-linejoin', 'round')
      .style('stroke-width', '1px')
      .attr('transform', `translate(${this.svgPadding}, ${this.svgPadding})`)
      .attr('fill', 'none');
  }

  createDataLayerOnChart = (data, colorScale, pixelG, defs, type) => {
    pixelG.attr(
      'transform',
      `translate(${this.svgPadding}, ${this.svgPadding})`
    );
    const rectData = [];
    data.forEach((entry) => {
      rectData.push({
        type: 'LineString',
        coordinates: entry.corners,
        extras: {
          value: entry.value,
        },
      });
    });

    pixelG
      .selectAll('path')
      .data(rectData)
      .enter()
      .append('path')
      .attr('class', 'route')
      .attr('d', this.geopath)
      .attr('value', (d) => d.extras.value)
      .style('fill', (d) => colorScale(d.extras.value))
      .style('shape-rendering', 'crispedges');
  };

  generateColorScale(limitToggle, colorset) {
    let domainQuantile;
    let domainLegend;
    let rangeQuantile;
    let steps;

    const [useColors] = colorset;

    if (limitToggle === 'absolute') {
      domainQuantile = useColors.absolutLimit.split(',').map(Number);
      domainLegend = useColors.absolutLimit.split(',').map(Number);
      rangeQuantile = useColors.absolutColor.split(',');
      steps = useColors.absolutLimit.split(',').map(Number).length;
    } else {
      domainQuantile = useColors.changeLimit.split(',').map(Number);
      rangeQuantile = useColors.changeColor.split(',');
      steps = useColors.changeLimit.split(',').map(Number).length;
      domainLegend = useColors.changeLimit.split(',').map(Number);
    }

    const colorScale = d3
      .scaleQuantile()
      .domain(domainQuantile)
      .range(rangeQuantile);
    return { domainLegend, rangeQuantile, steps, colorScale };
  }

  render() {
    const chartItems = [
      {
        id: 'reference',
        copyright: true,
        html: this.captionFirstRCP,
      },
      {
        id: 'future',
        copyright: false,
        html: this.captionSecondRCP,
      },
      {
        id: 'difference',
        copyright: false,
        html: this.captionDifference,
      },
    ];
    const { isLoading } = this.state;
    const { chartTitle, chartSubtitle } = this.props;

    if (!isLoading) {
      return (
        <div>
          <div ref={this.placeholderMap}>
            <div className="charts-container">
              <h3 className="chart-title">{chartTitle}</h3>
              <h5 className="chart-subtitle">{chartSubtitle}</h5>
              <div className="vis-wrapper" id="vis-wrapper">
                {chartItems.map((it, index) => {
                  const key = `key-${index}`;

                  return (
                    <Container key={key} id={it.id}>
                      <Chart id={it.id} copyright={it.copyright} />
                      <ShapeCaption html={it.html} />
                    </Container>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      );
    }
    return (
      <Loader
        type="Bars"
        color={colors.themeBase.accent}
        height={100}
        width={100}
        className="isLoading"
      />
    );
  }
}

MapChartComparison.propTypes = {
  firstRCP: PropTypes.string,
  secondRCP: PropTypes.string,
  dataFirstRCP: PropTypes.array,
  dataSecondRCP: PropTypes.array,
  dataComparison: PropTypes.array,
  latLonData: PropTypes.array,
  colorsets: PropTypes.array,
  colorsetsComparison: PropTypes.array,
  filteredShape: PropTypes.array,
  fullShape: PropTypes.object,
  activeRegion: PropTypes.number,
  chartTitle: PropTypes.string,
  chartSubtitle: PropTypes.string,
  selectedYear: PropTypes.string,
};

MapChartComparison.defaultProps = {
  firstRCP: '',
  secondRCP: '',
  dataFirstRCP: [],
  dataSecondRCP: [],
  dataComparison: [],
  latLonData: [],
  filteredShape: [],
  fullShape: {},
  activeRegion: 11,
  colorsets: [],
  colorsetsComparison: [],
  chartTitle: '',
  chartSubtitle: '',
  selectedYear: '',
};

export default MapChartComparison;
